<template>
  <div>
    <div v-if="items.length" class="flex flex-wrap gap-2 mt-1">
      <div
        v-for="a in items"
        :key="a.id"
        class="relative border rounded overflow-hidden bg-gray-50"
      >
        <!-- display_url, not file_url: for a HEIC they differ, and the
             original is the one a browser cannot render. -->
        <a :href="a.file_url" target="_blank" rel="noopener" :title="tooltip(a)">
          <img
            v-if="a.is_image && a.display_url"
            :src="a.display_url"
            :alt="a.caption || a.original_filename"
            class="object-cover"
            :class="thumbClass"
          />
          <!-- A PDF, or an image whose preview could not be derived. -->
          <span
            v-else
            class="flex items-center justify-center text-xs text-gray-600 px-2 text-center"
            :class="thumbClass"
          >{{ shortName(a.original_filename) }}</span>
        </a>
        <button
          v-if="canRemove"
          class="absolute top-0 right-0 bg-white/90 text-gray-700 text-xs px-1 leading-4 rounded-bl hover:text-red-700"
          title="Remove"
          :disabled="busyId === a.id"
          @click.prevent="remove(a)"
        >×</button>
      </div>
    </div>

    <div v-if="canUpload" class="mt-2">
      <label class="inline-flex items-center gap-2 text-sm cursor-pointer">
        <input
          ref="picker"
          type="file"
          multiple
          :accept="ACCEPT"
          class="hidden"
          @change="pick"
        />
        <span
          class="px-2 py-1 border rounded text-xs hover:bg-gray-50"
          :class="uploading ? 'opacity-50' : ''"
        >{{ uploading ? 'Uploading…' : 'Attach a file' }}</span>
        <span class="text-xs text-gray-500">
          PNG, JPEG, HEIC, GIF, WebP or PDF · up to 20 MB
        </span>
      </label>
      <p v-if="error" class="text-xs text-red-600 mt-1">{{ error }}</p>
    </div>

    <p v-else-if="!items.length && showEmpty" class="text-xs text-gray-400 mt-1">
      No attachments.
    </p>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch } from 'vue'
import { useStore } from 'vuex'

import { deleteAttachment, uploadAttachment } from '@/services/BackOffice'

// Mirrors Attachment.ALLOWED_MIME_TYPES. The server is the authority; this
// only keeps the file picker from offering what it would reject.
const ACCEPT = 'image/png,image/jpeg,image/heic,image/heif,image/webp,image/gif,application/pdf'

const props = defineProps({
  // The rows this strip renders. Owned by the parent so a page that
  // already fetched them in its payload does not fetch them again.
  attachments: { type: Array, default: () => [] },
  targetType: { type: String, default: '' },
  targetId: { type: [Number, String], default: null },
  canUpload: { type: Boolean, default: false },
  canRemove: { type: Boolean, default: false },
  size: { type: String, default: 'md' },
  showEmpty: { type: Boolean, default: false },
})
const emit = defineEmits(['changed'])

const store = useStore()
const token = () => store.getters['auth/showToken']

const items = ref([...props.attachments])
const uploading = ref(false)
const busyId = ref(null)
const error = ref('')
const picker = ref(null)

const thumbClass = computed(
  () => (props.size === 'sm' ? 'w-12 h-12' : 'w-20 h-20'),
)

// The parent owns the list; when it reloads its payload the strip has to
// follow, or a page refresh leaves stale thumbnails on screen.
watch(() => props.attachments, value => { items.value = [...(value || [])] })

function shortName(name) {
  const value = String(name || 'file')
  return value.length > 18 ? `${value.slice(0, 15)}…` : value
}

function tooltip(a) {
  const kb = a.size_bytes ? ` · ${Math.round(a.size_bytes / 1024)} KB` : ''
  const who = a.uploaded_by_display ? ` · ${a.uploaded_by_display}` : ''
  return `${a.original_filename || 'file'}${kb}${who}`
}

async function pick(event) {
  const files = Array.from(event.target.files || [])
  if (!files.length) return
  uploading.value = true
  error.value = ''
  try {
    // Sequential rather than parallel: the failure message has to name
    // the file it belongs to, and a batch of phone photos can be large.
    for (const file of files) {
      const created = await uploadAttachment(
        token(), props.targetType, props.targetId, file,
      )
      items.value = [...items.value, created]
    }
    emit('changed', items.value)
  } catch (e) {
    error.value = describe(e)
  } finally {
    uploading.value = false
    if (picker.value) picker.value.value = ''
  }
}

async function remove(a) {
  busyId.value = a.id
  error.value = ''
  try {
    await deleteAttachment(token(), a.id)
    items.value = items.value.filter(x => x.id !== a.id)
    emit('changed', items.value)
  } catch (e) {
    error.value = describe(e)
  } finally {
    busyId.value = null
  }
}

function describe(e) {
  const data = e?.response?.data
  if (data && data.file) return String(data.file)
  if (data && data.detail) return String(data.detail)
  if (e?.response?.status) {
    return `${e.response.status} ${e.response.statusText || ''}`.trim()
  }
  return String(e)
}
</script>
